/**
 * Backend `ErrorCode` → Persian copy for the panels.
 *
 * Lives here rather than in either panel because both of them show the same
 * failures and a clinic owner reading «موجودی کافی نیست» in the panel must not
 * read something else for the same code in the console. It was written for the
 * clinic panel first; keeping a second copy there is exactly the drift this
 * package exists to prevent.
 *
 * ── WHY THIS FILE EXISTS AT ALL ─────────────────────────────────────────────
 *
 * The API already returns finished Persian in `message` (see
 * `backend/src/common/errors/error-messages.fa.ts`), and the panel prefers that
 * string whenever it is present — the server knows more about the specific
 * failure than a static table can. This map is the fallback for the two cases
 * the server cannot cover:
 *
 *   1. **No response at all.** A dropped connection, a timeout, a CORS
 *      rejection. There is no body to read a message out of.
 *   2. **A body without Persian.** A proxy-generated 502 page, an old build of
 *      the API, or a bug that let an English framework message through.
 *
 * The strings are kept deliberately identical to the backend's so a clinic user
 * never sees two different sentences for the same failure depending on which
 * path produced it. When a code is added on the server, add it here too — the
 * panel falls back to `message` and then to `UNKNOWN`, so a missing entry
 * degrades quietly rather than breaking, but it degrades.
 */
export const ERROR_MESSAGES_FA = {
    // ── Auth / session ────────────────────────────────────────────────────────
    AUTH_REQUIRED: "برای ادامه وارد بشید",
    AUTH_INVALID: "برای ادامه دوباره وارد بشید",
    AUTH_STALE: "سطح دسترسی شما تغییر کرده. دوباره وارد بشید",
    AUTH_FORBIDDEN: "به این بخش دسترسی ندارید",
    PHONE_INVALID: "شماره موبایل معتبر نیست",
    OTP_INVALID: "کد واردشده درست نیست. کد پیامک‌شده رو دوباره بررسی کنید",
    OTP_EXPIRED: "مهلت این کد تمام شده. کد جدید بگیرید",
    OTP_REQUEST_LIMIT: "چند بار پشت سر هم کد درخواست کردید. کمی صبر کنید و دوباره امتحان کنید",
    OTP_COOLDOWN_ACTIVE: "برای دریافت کد جدید کمی صبر کنید",
    // Mirrors the backend: the lockout outlives a new code, so do not suggest one.
    OTP_TOO_MANY_ATTEMPTS: "چند بار کد اشتباه وارد شد؛ چند دقیقه صبر کنید و دوباره امتحان کنید",
    REFRESH_INVALID: "برای ادامه دوباره وارد بشید",
    REFRESH_EXPIRED: "مهلت ورود شما تمام شده. دوباره وارد بشید",
    REFRESH_REUSED: "به‌دلیل استفاده مشکوک از حساب، از همه دستگاه‌ها خارج شدید. دوباره وارد بشید",
    SESSION_NOT_FOUND: "ورود فعالی با این مشخصات پیدا نشد",
    SESSION_REVOKED: "از طریق دستگاه دیگری از این حساب خارج شدید",
    USER_NOT_FOUND: "کاربری با این مشخصات پیدا نشد",
    USER_SUSPENDED: "حساب شما غیرفعال شده. با پشتیبانی تماس بگیرید",
    TWO_FACTOR_REQUIRED: "برای این کار باید کد تأیید دومرحله‌ای رو وارد کنید",
    TWO_FACTOR_INVALID: "کد تأیید دومرحله‌ای نادرست است",
    TWO_FACTOR_ALREADY_ENABLED: "تأیید دومرحله‌ای از قبل فعاله",
    TWO_FACTOR_NOT_ENABLED: "تأیید دومرحله‌ای برای این حساب فعال نیست",
    PASSWORD_LOGIN_INVALID: "نام کاربری یا رمز عبور نادرست است",
    PASSWORD_LOGIN_LOCKED: "بعد از چند بار ورود ناموفق، ورود به حساب موقتاً بسته شده. کمی بعد دوباره امتحان کنید",
    // ── Tenancy / permissions ─────────────────────────────────────────────────
    PERMISSION_DENIED: "برای این کار دسترسی لازم رو ندارید",
    CLINIC_SCOPE_MISMATCH: "این اطلاعات متعلق به کلینیک دیگری است",
    CLINIC_CONTEXT_MISSING: "کلینیک فعال مشخص نیست. دوباره وارد بشید",
    SUPER_ADMIN_ONLY: "این بخش فقط برای مدیر سامانه در دسترس است",
    IMPERSONATION_NOT_ALLOWED: "در حالت ورود به‌جای کاربر، این کار مجاز نیست",
    DOCTOR_SCOPE_DENIED: "فقط به بیماران خودتون دسترسی دارید",
    // ── Clinic / staff / onboarding ───────────────────────────────────────────
    CLINIC_NOT_FOUND: "کلینیک پیدا نشد",
    CLINIC_SLUG_TAKEN: "این نشانی اینترنتی قبلاً ثبت شده. نشانی دیگه‌ای انتخاب کنید",
    CLINIC_SUSPENDED: "حساب کلینیک موقتاً غیرفعاله. با پشتیبانی تماس بگیرید",
    CLINIC_CLOSED: "این کلینیک بسته شده است",
    CLINIC_NOT_VERIFIED: "تا تأیید مدارک کلینیک، این بخش در دسترس نیست",
    CLINIC_ALREADY_VERIFIED: "مدارک این کلینیک قبلاً تأیید شده است",
    VERIFICATION_DOCUMENT_REQUIRED: "برای تأیید کلینیک، مدارک رو بارگذاری کنید",
    STAFF_NOT_FOUND: "این همکار در کلینیک پیدا نشد",
    STAFF_ALREADY_MEMBER: "کاربری با این شماره موبایل از قبل عضو کلینیک است",
    STAFF_SEAT_LIMIT_REACHED: "ظرفیت کاربران این پلن پر شده. برای اضافه کردن کاربر، پلن رو ارتقا بدید",
    STAFF_INVITE_INVALID: "این دعوت‌نامه معتبر نیست",
    STAFF_INVITE_EXPIRED: "مهلت این دعوت‌نامه تمام شده. دعوت‌نامه جدید بفرستید",
    STAFF_IS_LAST_OWNER: "کلینیک باید دست‌کم یک مالک داشته باشد",
    STAFF_NOT_DOCTOR: "این همکار به‌عنوان پزشک ثبت نشده است",
    ROLE_NOT_FOUND: "نقش پیدا نشد",
    ROLE_KEY_TAKEN: "نقشی با این شناسه از قبل وجود دارد",
    ROLE_IS_SYSTEM: "نقش‌های پیش‌فرض سامانه قابل ویرایش نیستند",
    ROLE_IN_USE: "این نقش به کارکنان اختصاص داده شده. قبل از حذف، نقش اون‌ها رو تغییر بدید",
    PERMISSION_KEY_UNKNOWN: "یکی از دسترسی‌های انتخاب‌شده شناخته نشد",
    // ── Subscription / billing / SMS wallet ───────────────────────────────────
    PLAN_NOT_FOUND: "پلن پیدا نشد",
    PLAN_NOT_AVAILABLE: "این پلن الان قابل انتخاب نیست",
    SUBSCRIPTION_NOT_FOUND: "اشتراکی برای این کلینیک ثبت نشده است",
    SUBSCRIPTION_INACTIVE: "اشتراک شما فعال نیست؛ برای ادامه اشتراک رو تمدید کنید",
    SUBSCRIPTION_PAST_DUE: "صورت‌حساب اشتراک شما پرداخت نشده است",
    TRIAL_ALREADY_USED: "دورهٔ آزمایشی این کلینیک قبلاً استفاده شده است",
    PLAN_LIMIT_REACHED: "به سقف استفاده از پلن فعلی رسیدید. برای ادامه، پلن رو ارتقا بدید",
    FEATURE_NOT_IN_PLAN: "این قابلیت در پلن فعلی شما نیست",
    INVOICE_NOT_FOUND: "صورت‌حساب پیدا نشد",
    INVOICE_ALREADY_PAID: "این صورت‌حساب قبلاً پرداخت شده است",
    PAYMENT_INIT_FAILED: "اتصال به درگاه پرداخت برقرار نشد؛ کمی بعد دوباره امتحان کنید",
    PAYMENT_VERIFY_FAILED: "پرداخت تأیید نشد. اگر پول از حسابتون کم شده، تا ۷۲ ساعت برمی‌گرده",
    PAYMENT_AMOUNT_MISMATCH: "مبلغ پرداخت‌شده با صورت‌حساب همخوانی ندارد",
    PAYMENT_ALREADY_VERIFIED: "این پرداخت قبلاً تأیید شده است",
    PAYMENT_CANCELLED: "پرداخت لغو شد",
    SMS_WALLET_INSUFFICIENT: "اعتبار پیامک کافی نیست؛ از بخش تنظیمات شارژ کنید",
    SMS_TOPUP_INVALID_AMOUNT: "مبلغ شارژ معتبر نیست",
    SMS_SEND_FAILED: "ارسال پیامک انجام نشد؛ کمی بعد دوباره امتحان کنید",
    SMS_OPTED_OUT: "این بیمار دریافت پیامک رو لغو کرده",
    // ── Patients ──────────────────────────────────────────────────────────────
    PATIENT_NOT_FOUND: "بیمار پیدا نشد",
    PATIENT_PHONE_TAKEN: "این شماره موبایل قبلاً برای بیمار دیگری ثبت شده است",
    PATIENT_NATIONAL_ID_TAKEN: "این کد ملی قبلاً برای بیمار دیگری ثبت شده است",
    PATIENT_NATIONAL_ID_INVALID: "کد ملی معتبر نیست",
    PATIENT_BLACKLISTED: "برای این بیمار در این کلینیک محدودیت ثبت شده است",
    PATIENT_HAS_SESSIONS: "این بیمار جلسه درمان ثبت‌شده داره و نمی‌شه حذفش کرد. می‌تونید پرونده‌اش رو بایگانی کنید",
    PATIENT_NOTE_NOT_FOUND: "یادداشت پیدا نشد",
    PATIENT_LINK_NOT_FOUND: "لینک پرونده بیمار پیدا نشد",
    PATIENT_LINK_ALREADY_EXISTS: "این پرونده قبلاً به حساب شما متصل شده است",
    // ── Photos / consent ──────────────────────────────────────────────────────
    PHOTO_NOT_FOUND: "عکس پیدا نشد",
    PHOTO_UPLOAD_FAILED: "بارگذاری عکس انجام نشد؛ دوباره امتحان کنید",
    PHOTO_MIME_NOT_ALLOWED: "فرمت این عکس پشتیبانی نمی‌شه؛ JPEG، PNG یا WebP بفرستید",
    PHOTO_TOO_LARGE: "حجم عکس بیش از حد مجاز است",
    PHOTO_MARKETING_CONSENT_MISSING: "برای استفادهٔ تبلیغاتی، رضایت کتبی بیمار ثبت نشده است",
    PHOTO_COMPARISON_INVALID: "برای مقایسه، دو عکس از یک بیمار انتخاب کنید",
    CONSENT_NOT_FOUND: "رضایت‌نامه پیدا نشد",
    CONSENT_ALREADY_SIGNED: "این رضایت‌نامه قبلاً امضا شده است",
    CONSENT_ALREADY_VOID: "این رضایت‌نامه قبلاً باطل شده است",
    CONSENT_SIGNATURE_REQUIRED: "ثبت امضا الزامی است",
    CONSENT_CHECKBOX_REQUIRED: "همه بندهای الزامی رو تأیید کنید",
    CONSENT_TEMPLATE_NOT_FOUND: "قالب رضایت‌نامه پیدا نشد",
    CONSENT_TEMPLATE_NOT_PUBLISHED: "این قالب رضایت‌نامه هنوز منتشر نشده است",
    CONSENT_VERSION_IMMUTABLE: "نسخهٔ منتشرشدهٔ رضایت‌نامه قابل ویرایش نیست؛ نسخهٔ جدید بسازید",
    // ── Scheduling ────────────────────────────────────────────────────────────
    APPOINTMENT_NOT_FOUND: "نوبت پیدا نشد",
    APPOINTMENT_SLOT_TAKEN: "این ساعت پر شده. یک زمان دیگه انتخاب کنید",
    APPOINTMENT_OUTSIDE_WORKING_HOURS: "این زمان خارج از ساعت کاری است",
    APPOINTMENT_DOCTOR_UNAVAILABLE: "پزشک در این زمان در دسترس نیست",
    APPOINTMENT_ROOM_UNAVAILABLE: "اتاق در این زمان در دسترس نیست",
    APPOINTMENT_IN_PAST: "زمان انتخاب‌شده گذشته است",
    APPOINTMENT_TOO_SOON: "برای این ساعت دیگه نمی‌تونید نوبت بگیرید. یک زمان دیرتر انتخاب کنید",
    APPOINTMENT_TOO_FAR: "رزرو برای این تاریخ هنوز باز نشده است",
    APPOINTMENT_INVALID_STATUS: "با وضعیت فعلی این نوبت، نمی‌تونید این تغییر رو انجام بدید",
    APPOINTMENT_NOT_CANCELLABLE: "این نوبت دیگه قابل لغو نیست",
    APPOINTMENT_REQUEST_NOT_FOUND: "درخواست نوبت پیدا نشد",
    APPOINTMENT_REQUEST_HANDLED: "این درخواست قبلاً بررسی شده است",
    BOOKING_PHONE_NOT_VERIFIED: "برای ثبت نوبت، شماره موبایل رو تأیید کنید",
    BOOKING_CLOSED: "نوبت‌دهی آنلاین این کلینیک الان فعال نیست",
    ROOM_NOT_FOUND: "اتاق پیدا نشد",
    WORKING_HOURS_INVALID: "ساعت کاری درست نیست. زمان پایان باید بعد از زمان شروع باشه",
    TIME_OFF_OVERLAPS: "این بازه با یک مرخصی دیگه تداخل داره",
    WAITLIST_ENTRY_NOT_FOUND: "درخواست موردنظر در فهرست انتظار پیدا نشد",
    // ── Procedures / treatment sessions ───────────────────────────────────────
    PROCEDURE_NOT_FOUND: "خدمت پیدا نشد",
    PROCEDURE_KEY_TAKEN: "خدمتی با این شناسه از قبل وجود دارد",
    PROCEDURE_NOT_ENABLED: "این خدمت در کلینیک شما فعال نیست",
    PROCEDURE_IS_GLOBAL: "خدمات فهرست عمومی قابل حذف نیستند. می‌تونید اون‌ها رو غیرفعال کنید",
    TREATMENT_NOT_FOUND: "خدمت انجام‌شده پیدا نشد",
    TREATMENT_ALREADY_FINALIZED: "این خدمت نهایی شده و قابل تغییر نیست",
    TREATMENT_CONSENT_REQUIRED: "برای ثبت این خدمت، رضایت‌نامه الزامی است",
    TREATMENT_DISCOUNT_INVALID: "مبلغ تخفیف معتبر نیست",
    INJECTION_MAP_INVALID: "نقشهٔ تزریق معتبر نیست",
    // ── Inventory ─────────────────────────────────────────────────────────────
    MATERIAL_NOT_FOUND: "کالا پیدا نشد",
    MATERIAL_IN_USE: "برای این کالا سری ساخت ثبت شده است و حذف نمی‌شه",
    LOT_NOT_FOUND: "سری ساخت پیدا نشد",
    LOT_EXPIRED: "تاریخ انقضای این سری ساخت گذشته است",
    LOT_QUARANTINED: "این سری ساخت در قرنطینه است و قابل مصرف نیست",
    LOT_DEPLETED: "موجودی این سری ساخت تمام شده است",
    INVENTORY_INSUFFICIENT: "موجودی کافی نیست",
    INVENTORY_QUANTITY_INVALID: "مقدار واردشده معتبر نیست",
    INVENTORY_MOVEMENT_IMMUTABLE: "گردش انبار قابل ویرایش نیست؛ برای اصلاح، سند اصلاحی ثبت کنید",
    SUPPLIER_NOT_FOUND: "تأمین‌کننده پیدا نشد",
    // ── Money: packages, payments, accounting ─────────────────────────────────
    PACKAGE_NOT_FOUND: "بسته خدمات پیدا نشد",
    PACKAGE_EXPIRED: "اعتبار این بسته خدمات تمام شده است",
    PACKAGE_EXHAUSTED: "همه جلسه‌های این بسته خدمات استفاده شده",
    PACKAGE_PROCEDURE_MISMATCH: "این خدمت جزو بسته خدمات بیمار نیست",
    PATIENT_PAYMENT_NOT_FOUND: "پرداخت پیدا نشد",
    PATIENT_PAYMENT_EXCEEDS_BALANCE: "مبلغ پرداخت از مانده بدهی بیشتره",
    AMOUNT_INVALID: "مبلغ واردشده معتبر نیست",
    EXPENSE_NOT_FOUND: "هزینه پیدا نشد",
    PAYROLL_ENTRY_NOT_FOUND: "فیش حقوقی پیدا نشد",
    PAYROLL_PERIOD_LOCKED: "این دورهٔ حقوق بسته شده و قابل تغییر نیست",
    REVENUE_SHARE_OVERLAPS: "بازه زمانی این سهم درآمد با بازه دیگه‌ای تداخل داره",
    REVENUE_SHARE_INVALID: "مقدار سهم درآمد معتبر نیست",
    REPORT_RANGE_INVALID: "بازهٔ گزارش معتبر نیست",
    INVALID_RANGE: "بازهٔ زمانی معتبر نیست",
    RANGE_TOO_LONG: "بازهٔ زمانی بیش از حد طولانی است؛ بازهٔ کوتاه‌تری انتخاب کنید",
    // ── CRM, loyalty, communications ──────────────────────────────────────────
    LEAD_NOT_FOUND: "درخواست مشاوره پیدا نشد",
    LEAD_ALREADY_CONVERTED: "برای این درخواست مشاوره قبلاً پرونده بیمار ایجاد شده است",
    SURVEY_NOT_FOUND: "نظرسنجی پیدا نشد",
    SURVEY_ALREADY_ANSWERED: "پاسخ شما قبلاً ثبت شده. ممنون از وقتی که گذاشتید",
    SURVEY_TOKEN_INVALID: "این لینک نظرسنجی معتبر نیست یا مهلتش تمام شده",
    REFERRAL_CODE_INVALID: "کد معرف معتبر نیست",
    REFERRAL_SELF: "نمی‌تونید کد معرف خودتون رو استفاده کنید",
    LOYALTY_INSUFFICIENT_BALANCE: "امتیاز بیمار برای این کار کافی نیست",
    REMINDER_NOT_FOUND: "یادآور پیدا نشد",
    REMINDER_ALREADY_SENT: "این یادآور قبلاً ارسال شده است",
    REMINDER_RULE_INVALID: "تنظیمات یادآوری درست نیست",
    MESSAGE_TEMPLATE_NOT_FOUND: "قالب پیام پیدا نشد",
    MESSAGE_TEMPLATE_PLACEHOLDER_UNKNOWN: "یکی از متغیرهای متن پیام شناخته نشد. متغیرها رو با فهرست مجاز مقایسه کنید",
    CAMPAIGN_NOT_FOUND: "پیامک گروهی پیدا نشد",
    CAMPAIGN_ALREADY_SENT: "این پیامک گروهی قبلاً ارسال شده است",
    CAMPAIGN_SEGMENT_EMPTY: "هیچ پرونده‌ای با این فیلترها پیدا نشد",
    // ── Magazine / AI content pipeline ────────────────────────────────────────
    BLOG_FEED_SOURCE_NOT_FOUND: "منبع خبری پیدا نشد",
    BLOG_FEED_SOURCE_URL_TAKEN: "این لینک منبع خبری از قبل در فهرست هست",
    // ── In-panel guidance / smart guide ───────────────────────────────────────
    GUIDANCE_KEY_LIMIT: "تعداد راهنماهای ثبت‌شده برای شما از حد مجاز گذشته است",
    GUIDE_TIP_UNKNOWN: "این پیشنهاد شناخته‌شده نیست",
    GUIDE_TIP_STALE: "این پیشنهاد دیگه با وضعیت کلینیک جور نیست. فهرست پیشنهادها به‌روز شد",
    GUIDE_READ_ONLY: "در حالت ورود به‌جای کاربر، پیشنهادها فقط نمایش داده می‌شن",
    // ── AI photo import ───────────────────────────────────────────────────────
    AI_IMPORT_UNAVAILABLE: "ثبت اطلاعات از روی عکس فعلاً در دسترس نیست",
    AI_IMPORT_CONSENT_REQUIRED: "قبل از اولین استفاده، مالک کلینیک باید شرایط ارسال عکس رو تأیید کنه",
    AI_IMPORT_CONSENT_STALE: "متن شرایط تغییر کرده. صفحه رو دوباره بارگذاری کنید و شرایط جدید رو تأیید کنید",
    AI_IMPORT_QUOTA_EXCEEDED: "سهمیهٔ ثبت اطلاعات از روی عکس برای این دوره تمام شده است",
    AI_IMPORT_DAILY_LIMIT: "امروز بیش از حد مجاز صفحه فرستاده شده است؛ فردا دوباره امتحان کنید",
    AI_IMPORT_TOO_MANY_PAGES: "تعداد عکس‌ها به سقف مجاز رسیده. برای عکس‌های باقی‌مانده، درخواست جدید ثبت اطلاعات از روی عکس بسازید",
    AI_IMPORT_PAGE_INVALID: "این فایل عکس قابل استفاده نیست. عکس دیگه‌ای انتخاب کنید",
    AI_IMPORT_PAGE_DUPLICATE: "این عکس رو قبلاً اضافه کردید",
    AI_IMPORT_JOB_NOT_FOUND: "این درخواست ثبت اطلاعات از روی عکس پیدا نشد یا متعلق به شما نیست",
    AI_IMPORT_JOB_STATE: "در این مرحله نمی‌تونید این کار رو انجام بدید",
    AI_IMPORT_JOB_BUSY: "ثبت قبلی از روی عکس هنوز تمام نشده. اول اون رو تمام یا لغو کنید",
    AI_IMPORT_CLINIC_BUSY: "عکس‌های دو درخواست قبلی هنوز در حال پردازشه. چند دقیقهٔ دیگه امتحان کنید",
    AI_IMPORT_BUSY_RETRY: "لحظه‌ای صبر کنید و دوباره امتحان کنید",
    AI_IMPORT_KIND_LOCKED: "بعد از ویرایش ردیف‌ها، نوع اطلاعات قابل تغییر نیست",
    AI_IMPORT_ROW_STALE: "این ردیف هم‌زمان تغییر کرده است؛ صفحه رو دوباره بارگذاری کنید",
    AI_IMPORT_COMMIT_IN_PROGRESS: "اطلاعات در حال ثبت شدنه. کمی صبر کنید",
    AI_IMPORT_UNDO_EXPIRED: "مهلت لغو ثبت این اطلاعات تمام شده",
    AI_IMPORT_PURGED: "مهلت نگهداری تمام شده و عکس‌ها و اطلاعات موقت پاک شده",
    AI_IMPORT_IMPERSONATION: "در حالت ورود به‌جای کاربر، نمی‌تونید از ثبت اطلاعات از روی عکس استفاده کنید",
    // ── Uploads / storage ─────────────────────────────────────────────────────
    UPLOAD_FILE_MISSING: "فایلی انتخاب نشده است",
    UPLOAD_KEY_NOT_FOUND: "فایل بارگذاری‌شده پیدا نشد",
    UPLOAD_KEY_PREFIX_INVALID: "مسیر فایل معتبر نیست",
    UPLOAD_MIME_NOT_ALLOWED: "فرمت این فایل پشتیبانی نمی‌شه",
    UPLOAD_TOO_LARGE: "حجم فایل بیش از حد مجاز است",
    STORAGE_UNAVAILABLE: "فضای ذخیره‌سازی در دسترس نیست؛ کمی بعد دوباره امتحان کنید",
    // ── Privacy / retention ───────────────────────────────────────────────────
    EXPORT_REQUEST_NOT_FOUND: "درخواست خروجی پیدا نشد",
    EXPORT_ALREADY_PENDING: "فایل خروجی درخواست قبلی هنوز در حال آماده‌سازیه. تا پایان اون صبر کنید",
    EXPORT_RATE_LIMITED: "خروجی کامل کلینیک رو هر ۲۴ ساعت یک بار می‌شه آماده کرد. فردا دوباره امتحان کنید",
    ERASURE_REQUEST_NOT_FOUND: "درخواست حذف اطلاعات پیدا نشد",
    ERASURE_BLOCKED_BY_RETENTION: "تا پایان مهلت نگهداری اسناد، حذف این اطلاعات ممکن نیست",
    RECYCLE_BIN_WINDOW_EXPIRED: "مهلت بازیابی این مورد تمام شده است",
    // ── Generic / transport ───────────────────────────────────────────────────
    BAD_REQUEST: "درخواست نامعتبر است",
    VALIDATION_FAILED: "اطلاعات واردشده کامل یا درست نیست",
    NOT_FOUND: "موردی پیدا نشد",
    FORBIDDEN: "به این بخش دسترسی ندارید",
    CONFLICT: "این تغییر با اطلاعات فعلی سازگار نیست. صفحه رو دوباره بارگذاری کنید",
    RATE_LIMITED: "تعداد درخواست‌ها بیش از حد مجاز است؛ کمی بعد امتحان کنید",
    PAYLOAD_TOO_LARGE: "حجم اطلاعات ارسالی بیش از حد مجاز است",
    REQUEST_TIMEOUT: "پاسخ‌گویی بیش از حد طول کشید؛ دوباره امتحان کنید",
    SERVICE_UNAVAILABLE: "این بخش موقتاً در دسترس نیست. کمی بعد دوباره امتحان کنید",
    INTERNAL_ERROR: "خطای غیرمنتظره‌ای رخ داد؛ اگر تکرار شد به پشتیبانی خبر بدید",
    // ── Client-side only. These never come from the server. ───────────────────
    NETWORK: "ارتباط با سرور برقرار نشد؛ اتصال اینترنت رو بررسی کنید",
    TIMEOUT: "پاسخ‌گویی بیش از حد طول کشید؛ دوباره امتحان کنید",
    CANCELLED: "درخواست لغو شد",
    MALFORMED: "پاسخ دریافت‌شده قابل استفاده نبود. دوباره امتحان کنید",
    UNKNOWN: "خطای غیرمنتظره‌ای رخ داد؛ اگر تکرار شد به پشتیبانی خبر بدید",
};
/** `true` when the panel has Persian copy for this code. */
export function isKnownErrorCode(code) {
    return typeof code === "string" && code in ERROR_MESSAGES_FA;
}
/**
 * The code to display for an error body.
 *
 * A service that throws `new ConflictException('CONSENT_ALREADY_SIGNED')`
 * without a `DomainException` produces `code: 'CONFLICT'` and puts the real
 * reason in `message`. Preferring a message that is itself a known code
 * recovers the specific failure instead of showing the generic 409 line.
 */
export function resolveErrorCode(code, message) {
    if (isKnownErrorCode(message))
        return message;
    return code;
}
/**
 * Persian text for a failure.
 *
 * Order matters: a server-supplied Persian sentence beats the static table,
 * because it may carry specifics ("موجودی کافی نیست: ۲ واحد باقی مانده") that
 * a code alone cannot express. The table is used when the server said nothing
 * useful, and `UNKNOWN` when neither had anything.
 */
export function messageForCode(code, serverMessage) {
    if (serverMessage &&
        serverMessage.trim() !== "" &&
        !isKnownErrorCode(serverMessage)) {
        return serverMessage;
    }
    return lookup(code) ?? lookup(serverMessage) ?? UNKNOWN_MESSAGE_FA;
}
const UNKNOWN_MESSAGE_FA = "خطای غیرمنتظره‌ای رخ داد؛ اگر تکرار شد به پشتیبانی خبر بدید";
/**
 * Named rather than inlined because `noUncheckedIndexedAccess` types every
 * lookup on this record as possibly-undefined, which is honest: the table is
 * open-ended and a code added on the server before it is added here must
 * degrade to the fallback rather than to the string `"undefined"`.
 */
function lookup(code) {
    return typeof code === "string" ? ERROR_MESSAGES_FA[code] : undefined;
}
//# sourceMappingURL=error-codes.js.map